import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { BankStatement, BankStatementUploadResult, BankTransaction, BankTransactionsSummary, PaginationMeta, StripePayoutImportResult } from '@/types'

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const bankReconciliationKeys = {
  statements: {
    all: ['bank_statements'] as const,
    lists: () => [...bankReconciliationKeys.statements.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...bankReconciliationKeys.statements.lists(), filters] as const,
    details: () => [...bankReconciliationKeys.statements.all, 'detail'] as const,
    detail: (id: number) => [...bankReconciliationKeys.statements.details(), id] as const,
  },
  transactions: {
    all: (statementId: number) => ['bank_statements', statementId, 'transactions'] as const,
    list: (statementId: number, filters: Record<string, unknown>) =>
      [...bankReconciliationKeys.transactions.all(statementId), filters] as const,
  },
  allTransactions: {
    all: ['bank_transactions'] as const,
    lists: () => [...bankReconciliationKeys.allTransactions.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...bankReconciliationKeys.allTransactions.lists(), filters] as const,
  },
}

// ─── Statement Hooks ──────────────────────────────────────────────────────────

export function useBankStatements(page = 1) {
  return useQuery({
    queryKey: bankReconciliationKeys.statements.list({ page }),
    queryFn: async () => {
      const { data } = await api.get<{ data: BankStatement[]; meta: PaginationMeta }>(
        '/api/bank_statements',
        { params: { page } }
      )
      return data
    },
  })
}

export interface BankReconciliationTieOut {
  statement_id: number
  bank_name: BankStatement['bank_name']
  bank_account_number: string | null
  as_of_date: string
  statement_balance: number
  ledger_balance: number | null
  difference: number | null
  matched: boolean | null
  opening_balance_posted: boolean
  opening_balance_as_of_date: string | null
}

// ─── Transaction Hooks ────────────────────────────────────────────────────────
//
// EP-116 T028: a per-statement useBankStatement(id)/useBankTransactions(id)
// pair used to live here — no consumer ever called them. BankReconciliationPage
// works entirely through the account-wide, cross-statement views below
// (useBankStatements list + useBankTransactionsByMonth); no route or page for
// a single-statement drill-down exists in App.tsx. Removed 2026-08 (T028).
// bankReconciliationKeys.statements.detail / .transactions.* stay — they're
// still read by useMatchTransaction/useUnmatchTransaction's cache invalidation.

export function useBankTransactionsByMonth(
  month: string,
  matchStatus?: string,
  page = 1,
  // EP-97: user-selectable server-side sort (transaction_date | amount). Absent
  // when the view sits at the default (clean URL) — the BE falls back to
  // transaction_date desc.
  sortParams?: { sort?: string; sort_order?: string }
) {
  return useQuery({
    queryKey: bankReconciliationKeys.allTransactions.list({ month, matchStatus, page, ...sortParams }),
    queryFn: async () => {
      const params: Record<string, unknown> = { month, page }
      if (matchStatus) params.match_status = matchStatus
      if (sortParams?.sort) params.sort = sortParams.sort
      if (sortParams?.sort_order) params.sort_order = sortParams.sort_order
      const { data } = await api.get<{
        data: BankTransaction[]
        summary: BankTransactionsSummary
        // Latest account statement, independent of month, row filters and page.
        // Amounts and equality are calculated by the server.
        tie_out?: BankReconciliationTieOut | null
        meta: PaginationMeta
      }>('/api/bank_transactions', { params })
      return data
    },
    enabled: !!month,
  })
}

// ─── Upload Hook ──────────────────────────────────────────────────────────────

export interface UploadBankStatementInput {
  file: File
  bank_name: 'scb' | 'kbank' | 'bbl'
  password?: string
}

export function useUploadBankStatement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UploadBankStatementInput) => {
      const formData = new FormData()
      formData.append('file', input.file)
      formData.append('bank_name', input.bank_name)
      if (input.password) formData.append('password', input.password)
      // T131-006: the 201 response also carries import_summary (row-level
      // dedup counts) and warning (overlapping-statement notice, or null) —
      // return the full body so the caller can surface both instead of
      // discarding them.
      const { data } = await api.post<BankStatementUploadResult>(
        '/api/bank_statements',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    },
    onSuccess: () => {
      removeListQueries(queryClient, bankReconciliationKeys.statements.all)
      removeListQueries(queryClient, bankReconciliationKeys.allTransactions.all)
    },
  })
}

// ─── Stripe Payout Import Hook ────────────────────────────────────────────────

export interface ImportStripePayoutInput {
  file: File
  currency?: string
}

export function useImportStripePayouts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ImportStripePayoutInput) => {
      const formData = new FormData()
      formData.append('file', input.file)
      if (input.currency) formData.append('currency', input.currency)
      const { data } = await api.post<StripePayoutImportResult>(
        '/api/stripe_payouts',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    },
    // UploadStripePayoutModal renders a dedicated inline error Alert for
    // every failure code, so the global mutation-error backstop would
    // otherwise add its own toast on top of that alert (T149-010).
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      // Invalidate bank transactions so any newly matched deposit lines refresh.
      removeListQueries(queryClient, bankReconciliationKeys.allTransactions.all)
    },
  })
}

// ─── Candidate Payments Hook ─────────────────────────────────────────────────

export interface CandidatePayment {
  id: number
  amount: number
  paid_at: string
  payment_method: string
  trans_ref: string | null
  // T131-008/009: whether this candidate's resolved customer name agrees with
  // the bank row's parsed payer name. Absence of a name-match badge is the
  // normal case (most candidates carry no name signal at all) — never render
  // a negative/red badge for false, only a positive badge for true.
  name_match: boolean
  // Who the payment is from and what it settles. Amount + method + reference
  // cannot tell two same-amount rents apart, so without these the operator has
  // to leave the page to identify a candidate before choosing it.
  customer_name: string | null
  invoice_id: number | null
  invoice_number: string | null
  // Slip images for this payment, in the same resolution order the payment
  // detail page uses. Either full URLs or bare storage keys — resolve through
  // /api/images/:key before rendering.
  slip_images: string[]
}

// T131-011: the /candidates response for a CREDIT row also carries a sibling
// `suggested_customer_id` — a single unambiguous customer resolved from the
// bank row's parsed payer name against the account's tenant roster,
// independent of whether the payments candidate pool above is empty. Shared
// query (same key) between useCandidatePayments and useSuggestedCustomerId via
// `select` so both hooks reuse ONE network fetch.
function candidatesQueryOptions(transactionId: number | null) {
  return {
    queryKey: ['bank_transactions', transactionId, 'candidates'] as const,
    queryFn: async () => {
      const { data } = await api.get<{ data: CandidatePayment[]; suggested_customer_id?: number | null }>(
        `/api/bank_transactions/${transactionId}/candidates`
      )
      return data
    },
    enabled: !!transactionId,
  }
}

export function useCandidatePayments(transactionId: number | null) {
  return useQuery({
    ...candidatesQueryOptions(transactionId),
    select: (data) => data.data,
  })
}

// T131-011/T131-012: independent of the payments candidate pool, lets the
// "Record payment" CTA pre-select who paid even when there is nothing to
// match against yet. null when the description carries no usable payer name,
// or when it matches more than one customer (never an arbitrary pick).
export function useSuggestedCustomerId(transactionId: number | null) {
  return useQuery({
    ...candidatesQueryOptions(transactionId),
    select: (data) => data.suggested_customer_id ?? null,
  })
}

// ─── Candidate Expenses Hook ──────────────────────────────────────────────────
// T128-010: mirrors useCandidatePayments for debit transactions. The same
// /api/bank_transactions/:id/candidates endpoint is direction-aware (T128-004):
// it returns Expense candidates when the transaction is a debit row.

export interface CandidateExpense {
  id: number
  expense_date: string
  total_amount: number
  net_cash_amount: number
  category: string
  vendor_name: string | null
  description: string | null
  property_name: string | null
}

export function useCandidateExpenses(transactionId: number | null) {
  return useQuery({
    queryKey: ['bank_transactions', transactionId, 'candidates'] as const,
    queryFn: async () => {
      const { data } = await api.get<{ data: CandidateExpense[] }>(
        `/api/bank_transactions/${transactionId}/candidates`
      )
      return data.data
    },
    enabled: !!transactionId,
  })
}

// ─── Match / Unmatch Hooks ────────────────────────────────────────────────────

// T128-010: Generalised match input. Two shapes are accepted:
//   New (preferred):  { transactionId, matchableType, matchableId }
//   Legacy (kept for backward compat with existing page callers):
//                     { transactionId, paymentId }
//
// When paymentId is supplied without matchableType/matchableId, the hook
// converts it internally to the Payment path (matching the server-side alias).
// T128-011 will migrate the page to the new shape; until then both compile.
export type MatchTransactionInput =
  | { transactionId: number; matchableType: 'Payment' | 'Expense'; matchableId: number }
  | { transactionId: number; paymentId: number }

export function useMatchTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: MatchTransactionInput) => {
      let body: Record<string, unknown>
      if ('paymentId' in input) {
        // Legacy path: send payment_id for backward compat with server alias
        body = { payment_id: input.paymentId }
      } else if (input.matchableType === 'Payment') {
        // New path — Payment: send both the new params and the legacy alias so
        // older server versions keep working during the rollout window.
        body = {
          matchable_type: 'Payment',
          matchable_id: input.matchableId,
          payment_id: input.matchableId,
        }
      } else {
        // New path — Expense (debit rows)
        body = { matchable_type: 'Expense', matchable_id: input.matchableId }
      }
      const { data } = await api.patch<{ data: BankTransaction }>(
        `/api/bank_transactions/${input.transactionId}/match`,
        body
      )
      return data.data
    },
    onSuccess: (transaction) => {
      removeListQueries(queryClient, bankReconciliationKeys.transactions.all(transaction.bank_statement_id))
      invalidateDetailQueries(queryClient, bankReconciliationKeys.statements.detail(transaction.bank_statement_id))
      // invalidate (not remove): the month summary is an ACTIVELY observed
      // query — removeQueries leaves its observer rendering the last result
      // without a refetch, so the stat cards/tab badges went stale after
      // match/unmatch (caught in CiC verification). invalidateQueries
      // refetches active observers and marks inactive ones stale.
      queryClient.invalidateQueries({ queryKey: bankReconciliationKeys.allTransactions.all })
    },
  })
}

export function useUnmatchTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (transactionId: number) => {
      const { data } = await api.delete<{ data: BankTransaction }>(
        `/api/bank_transactions/${transactionId}/match`
      )
      return data.data
    },
    onSuccess: (transaction) => {
      removeListQueries(queryClient, bankReconciliationKeys.transactions.all(transaction.bank_statement_id))
      invalidateDetailQueries(queryClient, bankReconciliationKeys.statements.detail(transaction.bank_statement_id))
      // invalidate (not remove): the month summary is an ACTIVELY observed
      // query — removeQueries leaves its observer rendering the last result
      // without a refetch, so the stat cards/tab badges went stale after
      // match/unmatch (caught in CiC verification). invalidateQueries
      // refetches active observers and marks inactive ones stale.
      queryClient.invalidateQueries({ queryKey: bankReconciliationKeys.allTransactions.all })
    },
  })
}

// ─── Ignore / Unignore Hooks (T131-016) ──────────────────────────────────────
//
// T131-015's terminal 'ignored' match_status, set aside per-row by the
// operator (never auto-classified). Mirrors useMatchTransaction /
// useUnmatchTransaction's cache-invalidation shape so the current tab's row
// list and the tab-count badges (both driven off allTransactions) refresh
// without a full page reload.

export function useIgnoreTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (transactionId: number) => {
      const { data } = await api.patch<{ data: BankTransaction }>(
        `/api/bank_transactions/${transactionId}/ignore`
      )
      return data.data
    },
    onSuccess: (transaction) => {
      removeListQueries(queryClient, bankReconciliationKeys.transactions.all(transaction.bank_statement_id))
      invalidateDetailQueries(queryClient, bankReconciliationKeys.statements.detail(transaction.bank_statement_id))
      queryClient.invalidateQueries({ queryKey: bankReconciliationKeys.allTransactions.all })
    },
  })
}

export function useUnignoreTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (transactionId: number) => {
      const { data } = await api.patch<{ data: BankTransaction }>(
        `/api/bank_transactions/${transactionId}/unignore`
      )
      return data.data
    },
    onSuccess: (transaction) => {
      removeListQueries(queryClient, bankReconciliationKeys.transactions.all(transaction.bank_statement_id))
      invalidateDetailQueries(queryClient, bankReconciliationKeys.statements.detail(transaction.bank_statement_id))
      queryClient.invalidateQueries({ queryKey: bankReconciliationKeys.allTransactions.all })
    },
  })
}
